import { Glasses } from 'lucide-react';
import { Scan } from '../../types';
import { formatDateTime } from '../../utils/formatting';

type IssueSeverity = 'critical' | 'serious' | 'moderate' | 'minor';

interface ResultsSummaryProps {
  scan: Scan;
  pageCount: number;
  onSeverityClick?: (severity: IssueSeverity) => void;
  /** Links already checked by hand in this scan — subtracted from the manual-review total */
  reviewedLinkCount?: number;
}

export function ResultsSummary({
  scan,
  pageCount,
  onSeverityClick,
  reviewedLinkCount = 0,
}: ResultsSummaryProps) {
  const { results } = scan;

  // The scan counted every item needing a human check; what the header should show is
  // what is still left to check. Reviewing a link is per-scan, so this is display
  // arithmetic, not a stored number — the next scan starts from the full count again.
  const manualReviewRemaining = Math.max(0, (results?.manualReviewCount ?? 0) - reviewedLinkCount);

  if (!results) return null;

  const issues = results.uniqueIssues || results.totalIssues;

  const SeverityButton = ({
    severity,
    count,
    colorClass,
    label,
  }: {
    severity: IssueSeverity;
    count: number;
    colorClass: string;
    label: string;
  }) => (
    <button
      onClick={() => onSeverityClick?.(severity)}
      className={`flex items-center gap-2 group ${onSeverityClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
      disabled={!onSeverityClick || count === 0}
    >
      <span className={`w-3 h-3 rounded-full ${colorClass}`} />
      <span
        className={`text-white font-medium ${onSeverityClick && count > 0 ? 'group-hover:underline' : ''}`}
      >
        {count}
      </span>
      <span className="text-white/70">{label}</span>
    </button>
  );

  return (
    <div className="section-dark rounded-card p-8 mb-8">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        <div>
          <h1 className="text-heading-1 font-bold text-white mb-2">Scan Results</h1>
          <p className="text-body text-white/70 mb-4">{scan.config.siteUrl}</p>
          <div className="flex flex-wrap gap-4 text-body-sm text-white/50">
            <span>
              WCAG {scan.config.wcagVersion} {scan.config.wcagLevel}
            </span>
            <span>•</span>
            <span>{pageCount} pages scanned</span>
            <span>•</span>
            <span>{formatDateTime(scan.completedAt || scan.startedAt)}</span>
          </div>
        </div>
      </div>

      {/* Issue Summary Bar - Clickable */}
      <div className="mt-8 pt-6 border-t border-white/10">
        <div className="flex flex-wrap gap-6">
          <SeverityButton
            severity="critical"
            count={issues.critical}
            colorClass="bg-severity-critical"
            label="Critical"
          />
          <SeverityButton
            severity="serious"
            count={issues.serious}
            colorClass="bg-severity-serious"
            label="Serious"
          />
          <SeverityButton
            severity="moderate"
            count={issues.moderate}
            colorClass="bg-severity-moderate"
            label="Moderate"
          />
          <SeverityButton
            severity="minor"
            count={issues.minor}
            colorClass="bg-severity-minor"
            label="Minor"
          />
          {results.manualReviewCount ? (
            <span
              className="flex items-center gap-2"
              title={
                reviewedLinkCount > 0
                  ? `${manualReviewRemaining} still to check, ${reviewedLinkCount} already reviewed in this scan. Excluded from the score and grade.`
                  : 'Items that need a human check — excluded from the score and grade'
              }
            >
              <Glasses size={16} className="text-white/60" />
              <span className="text-white font-medium">{manualReviewRemaining}</span>
              <span className="text-white/70">Manual review</span>
            </span>
          ) : null}
          <button
            onClick={() => onSeverityClick?.('critical')} // This will show all issues
            className={`flex items-center gap-2 ml-auto ${onSeverityClick ? 'cursor-pointer hover:opacity-80 transition-opacity group' : ''}`}
            disabled={!onSeverityClick || issues.total === 0}
          >
            <span
              className={`text-white font-bold text-heading-3 ${onSeverityClick && issues.total > 0 ? 'group-hover:underline' : ''}`}
            >
              {issues.total}
            </span>
            <span className="text-white/70">Total Issues</span>
          </button>
        </div>
        {onSeverityClick && issues.total > 0 && (
          <p className="text-white/50 text-body-sm mt-2">Click on a severity to filter issues</p>
        )}
      </div>
    </div>
  );
}
